import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Save, Upload, X, Maximize, CheckCircle, Bed, Bath, Droplets, Car,
  Building, Sun, Zap, Shield, Leaf, Ruler, Home, Layers, Box, HelpCircle, GripVertical, Star
} from 'lucide-react';
import {
  DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { authService } from '../../services/auth';
import { api, formatImageUrl } from '../../services/api';

const SortableImage = ({ id, src, isMain, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group">
      <img src={src} alt="" className="w-full h-32 object-cover rounded-lg pointer-events-none" />

      <div
        {...attributes}
        {...listeners}
        className="absolute inset-0 rounded-lg cursor-grab active:cursor-grabbing"
        title="Arrastrar para reordenar"
      />

      <div className="absolute top-1 left-1 p-1 bg-black/40 rounded text-white pointer-events-none">
        <GripVertical className="w-4 h-4" />
      </div>

      <button
        type="button"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity z-10"
        title="Quitar esta foto"
      >
        <X className="w-4 h-4" />
      </button>

      {isMain && (
        <div className="absolute bottom-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded pointer-events-none">
          Principal
        </div>
      )}
    </div>
  );
};

// Mismo set de íconos que usa PropertyDetailsModal para la vista pública
const FEATURE_ICONS = {
  maximize: Maximize,
  'check-circle': CheckCircle,
  bed: Bed,
  bath: Bath,
  droplets: Droplets,
  car: Car,
  building: Building,
  sun: Sun,
  zap: Zap,
  shield: Shield,
  tree: Leaf,
  ruler: Ruler,
  home: Home,
  layers: Layers,
  box: Box
};

const ICON_OPTIONS = [
  { value: 'maximize', label: 'Superficie / m²' },
  { value: 'ruler', label: 'Medidas' },
  { value: 'bed', label: 'Dormitorios' },
  { value: 'bath', label: 'Baños (bañera)' },
  { value: 'droplets', label: 'Baños (gotas)' },
  { value: 'home', label: 'Casa' },
  { value: 'building', label: 'Edificio' },
  { value: 'layers', label: 'Plantas / Pisos' },
  { value: 'car', label: 'Cochera' },
  { value: 'sun', label: 'Orientación / Sol' },
  { value: 'zap', label: 'Servicios' },
  { value: 'shield', label: 'Seguridad' },
  { value: 'tree', label: 'Jardín / Árboles' },
  { value: 'box', label: 'General' },
  { value: 'check-circle', label: 'Check / Otro' }
];

const parsePrice = (priceStr) => {
  if (!priceStr) return { currency: 'USD', priceAmount: '' };
  const currency = /ars|\$(?!.*usd)/i.test(priceStr) && !/usd|u\$s/i.test(priceStr) ? 'ARS' : 'USD';
  const priceAmount = priceStr.replace(/[^\d]/g, '');
  return { currency, priceAmount };
};

const formatPrice = (currency, priceAmount) => {
  const num = Number(String(priceAmount).replace(/[^\d]/g, ''));
  if (!num) return '';
  // Siempre formato argentino (punto de miles), sea USD o ARS
  const formatted = num.toLocaleString('es-AR');
  return `${currency} ${formatted}`;
};

const COUNT_OPTIONS = Array.from({ length: 11 }, (_, i) => i); // 0 a 10
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR + 2 - 1900 + 1 }, (_, i) => CURRENT_YEAR + 2 - i); // descendente

const FeatureIconPreview = ({ iconName }) => {
  const Icon = FEATURE_ICONS[iconName?.trim().toLowerCase()] || HelpCircle;
  return (
    <div className="flex items-center justify-center w-10 h-10 bg-gray-50 border border-gray-200 rounded-lg flex-shrink-0">
      <Icon className="w-5 h-5 text-primary" />
    </div>
  );
};

const PropertyForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const canEditContactPerson = authService.getUser()?.role === 'admin';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    currency: 'USD',
    priceAmount: '',
    original_price: '',
    location: '',
    address: '',
    mapsUrl: '',
    category: 'casa',
    type: 'venta',
    status: 'disponible',
    bedrooms: 0,
    bathrooms: 0,
    area: '',
    total_area: '',
    year_built: new Date().getFullYear(),
    floors: 1,
    garage_spots: 0,
    contactPerson: authService.getUser()?.name || '',
    contactPhone: authService.getUser()?.phone || '',
    contactAvatar: authService.getUser()?.avatar || '',
    featured: false,
    amenities: [],
    features: [
      { icon: 'maximize', label: 'Superficie', value: '' },
      { icon: 'home', label: 'Dormitorios', value: '' },
      { icon: 'droplets', label: 'Baños', value: '' }
    ]
  });
  
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [newAmenity, setNewAmenity] = useState('');

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
  );

  const handleExistingDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setExistingImages((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
      setIsDirty(true);
    }
  };

  const handleNewImagesDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = Number(active.id);
      const newIndex = Number(over.id);
      setImages((items) => arrayMove(items, oldIndex, newIndex));
      setImagePreviews((items) => arrayMove(items, oldIndex, newIndex));
      setIsDirty(true);
    }
  };
  
  // Cargar propiedad si estamos en modo edición
  useEffect(() => {
    if (isEditMode) {
      loadProperty();
    }
  }, [id]);
  
  const loadProperty = async () => {
    try {
      setLoading(true);
      const property = await api.getProperty(id);
      
      if (property) {
        const { currency, priceAmount } = parsePrice(property.price);
        setFormData({
          title: property.title || '',
          description: property.description || '',
          currency,
          priceAmount,
          original_price: property.original_price || '',
          location: property.location || '',
          address: property.address || '',
          mapsUrl: property.mapsUrl || '',
          category: property.category || 'casa',
          type: property.type || 'venta',
          status: property.status || 'disponible',
          bedrooms: property.bedrooms || 0,
          bathrooms: property.bathrooms || 0,
          area: property.area || '',
          total_area: property.total_area || '',
          year_built: property.year_built || new Date().getFullYear(),
          floors: property.floors || 1,
          garage_spots: property.garage_spots || 0,
          contactPerson: property.contactPerson || authService.getUser()?.name || '',
          contactPhone: property.contactPhone || authService.getUser()?.phone || '',
          contactAvatar: property.contactAvatar || authService.getUser()?.avatar || '',
          featured: property.featured || false,
          amenities: property.amenities || [],
          features: property.features || [
            { icon: 'maximize', label: 'Superficie', value: property.area || '' },
            { icon: 'home', label: 'Dormitorios', value: property.bedrooms || '' },
            { icon: 'droplets', label: 'Baños', value: property.bathrooms || '' }
          ]
        });
        
        // Si hay imágenes en la propiedad
        if (property.images && property.images.length > 0) {
          setExistingImages(property.images);
        }
      }
    } catch (error) {
      console.error('Error cargando propiedad:', error);
      setError('Error al cargar la propiedad');
    } finally {
      setLoading(false);
    }
  };
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setIsDirty(true);
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value === '' ? '' : parseInt(value) || 0
    }));
    setIsDirty(true);
  };

  const handleFeatureChange = (index, field, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index][field] = value;
    setFormData(prev => ({ ...prev, features: newFeatures }));
    setIsDirty(true);
  };

  const addFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, { icon: 'check-circle', label: '', value: '' }]
    }));
    setIsDirty(true);
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
    setIsDirty(true);
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setImages(prev => [...prev, ...files]);
    setIsDirty(true);

    // Crear previews
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    const newImages = [...images];
    const newPreviews = [...imagePreviews];

    newImages.splice(index, 1);
    newPreviews.splice(index, 1);

    setImages(newImages);
    setImagePreviews(newPreviews);
    setIsDirty(true);
  };

  const removeExistingImage = (index) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
    setIsDirty(true);
  };

  const addAmenity = () => {
    if (newAmenity.trim()) {
      setFormData(prev => ({
        ...prev,
        amenities: [...prev.amenities, newAmenity.trim()]
      }));
      setNewAmenity('');
      setIsDirty(true);
    }
  };

  const removeAmenity = (index) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.filter((_, i) => i !== index)
    }));
    setIsDirty(true);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.priceAmount || !formData.location || !formData.category) {
      setError('Por favor completa los campos requeridos (título, precio, ubicación, categoría)');
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      const token = authService.getToken();
      if (!token) {
        throw new Error('No estás autenticado');
      }
      
      const formDataToSend = new FormData();
      formDataToSend.append('price', formatPrice(formData.currency, formData.priceAmount));

      // Agregar datos del formulario
      Object.keys(formData).forEach(key => {
        if (key === 'amenities' || key === 'features') {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else if (formData[key] !== null && formData[key] !== undefined) {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      // Imágenes existentes que se mantienen (las que no se sacaron con el botón de quitar)
      if (isEditMode) {
        formDataToSend.append('existingImages', JSON.stringify(existingImages));
      }

      // Agregar imágenes nuevas
      images.forEach((image, index) => {
        formDataToSend.append('images', image);
      });
      
      const url = isEditMode 
        ? `/api/properties/${id}`
        : '/api/properties';
      
      const method = isEditMode ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess(isEditMode ? '✅ Propiedad actualizada exitosamente' : '✅ Propiedad creada exitosamente');
        setIsDirty(false);

        // El overlay de "guardando" se mantiene visible hasta redirigir
        setTimeout(() => {
          navigate('/admin/properties');
        }, 1500);
      } else {
        setError(data.error || 'Error al guardar la propiedad');
        setSaving(false);
      }
    } catch (error) {
      console.error('Error guardando propiedad:', error);
      setError(error.message || 'Error al guardar la propiedad');
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`max-w-6xl mx-auto ${isDirty && !saving ? 'pb-24' : ''}`}>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">
          {isEditMode ? 'Editar Propiedad' : 'Nueva Propiedad'}
        </h1>
        <p className="text-gray-600">
          {isEditMode ? 'Modifica los datos de la propiedad' : 'Completa el formulario para agregar una nueva propiedad'}
        </p>
      </div>
      
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <X className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-700 font-medium">{error}</span>
          </div>
        </div>
      )}
      
      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <Save className="w-5 h-5 text-green-600 mr-2" />
            <span className="text-green-700 font-medium">{success}</span>
          </div>
        </div>
      )}
      
      <form id="property-form" onSubmit={handleSubmit} className="space-y-8">
        {/* Información Básica */}
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">Información Básica</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Título *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="Ej: Casa moderna en Nordelta"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Precio *
              </label>
              <div className="flex gap-2">
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  className="px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                >
                  <option value="USD">USD</option>
                  <option value="ARS">ARS</option>
                </select>
                <input
                  type="number"
                  name="priceAmount"
                  value={formData.priceAmount}
                  onChange={handleChange}
                  min="0"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="Ej: 150000"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ubicación / Barrio - Provincia *
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="Ej: Tres Cerritos, Salta"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dirección
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="Dirección completa"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Link de Google Maps (ubicación exacta)
              </label>
              <input
                type="text"
                name="mapsUrl"
                value={formData.mapsUrl}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="https://maps.app.goo.gl/..."
              />
              <p className="text-xs text-gray-500 mt-1">Pegá el link para compartir de Google Maps con el pin en la casa</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Persona de contacto
              </label>
              <input
                type="text"
                name="contactPerson"
                value={formData.contactPerson}
                onChange={handleChange}
                disabled={!canEditContactPerson}
                className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${
                  !canEditContactPerson ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                }`}
                placeholder="Nombre del agente"
              />
              <p className="text-xs text-gray-500 mt-1">
                {canEditContactPerson
                  ? 'Como administrador podés reasignar el contacto de esta propiedad'
                  : 'No editable: la comisión corresponde a quien publicó la propiedad'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Teléfono de contacto
              </label>
              <input
                type="text"
                name="contactPhone"
                value={formData.contactPhone}
                onChange={handleChange}
                disabled={!canEditContactPerson}
                className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${
                  !canEditContactPerson ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                }`}
                placeholder="+54 387 456-7890"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Categoría *
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                required
              >
                <option value="casa">Casa</option>
                <option value="departamento">Departamento</option>
                <option value="terreno">Terreno</option>
                <option value="local">Local Comercial</option>
                <option value="oficina">Oficina</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              >
                <option value="venta">Venta</option>
                <option value="alquiler">Alquiler</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estado
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              >
                <option value="disponible">Disponible</option>
                <option value="reservado">Reservado</option>
                <option value="vendido">Vendido</option>
                <option value="alquilado">Alquilado</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Publicación
              </label>
              <button
                type="button"
                role="switch"
                aria-checked={formData.featured}
                onClick={() => {
                  setFormData(prev => ({ ...prev, featured: !prev.featured }));
                  setIsDirty(true);
                }}
                className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-amber-400 ${
                  formData.featured
                    ? 'bg-amber-50 border-amber-300'
                    : 'bg-white border-gray-300 hover:border-gray-400'
                }`}
              >
                <span className="flex items-center gap-2">
                  <Star
                    className={`w-5 h-5 transition-colors ${
                      formData.featured ? 'text-amber-500 fill-amber-500' : 'text-gray-400'
                    }`}
                  />
                  <span className={`text-sm font-medium ${formData.featured ? 'text-amber-700' : 'text-gray-700'}`}>
                    Propiedad destacada
                  </span>
                </span>
                <span
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                    formData.featured ? 'bg-amber-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      formData.featured ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </span>
              </button>
            </div>
          </div>
        </div>
        
        {/* Descripción */}
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">Descripción</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descripción detallada
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="6"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              placeholder="Describe la propiedad en detalle..."
            />
          </div>
        </div>
        
        {/* Características */}
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">Características</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dormitorios
              </label>
              <select
                name="bedrooms"
                value={formData.bedrooms}
                onChange={handleNumberChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              >
                {COUNT_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Baños
              </label>
              <select
                name="bathrooms"
                value={formData.bathrooms}
                onChange={handleNumberChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              >
                {COUNT_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Superficie construida (m²)
              </label>
              <input
                type="number"
                name="area"
                value={formData.area}
                onChange={handleChange}
                step="0.01"
                min="0"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Superficie total (m²)
              </label>
              <input
                type="number"
                name="total_area"
                value={formData.total_area}
                onChange={handleChange}
                step="0.01"
                min="0"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Año de construcción
              </label>
              <select
                name="year_built"
                value={formData.year_built}
                onChange={handleNumberChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              >
                {YEAR_OPTIONS.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Plantas
              </label>
              <select
                name="floors"
                value={formData.floors}
                onChange={handleNumberChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              >
                {COUNT_OPTIONS.filter((n) => n >= 1).map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cocheras
              </label>
              <select
                name="garage_spots"
                value={formData.garage_spots}
                onChange={handleNumberChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              >
                {COUNT_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        
        {/* Comodidades */}
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">Comodidades</h2>
          <div className="mb-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={newAmenity}
                onChange={(e) => setNewAmenity(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="Ej: Pileta, Cochera, Jardín..."
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAmenity())}
              />
              <button
                type="button"
                onClick={addAmenity}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Agregar
              </button>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {formData.amenities.map((amenity, index) => (
              <div
                key={index}
                className="flex items-center bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg"
              >
                <span>{amenity}</span>
                <button
                  type="button"
                  onClick={() => removeAmenity(index)}
                  className="ml-2 text-blue-500 hover:text-blue-700"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
        
        {/* Características destacadas */}
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">Características Destacadas</h2>
          <div className="space-y-4">
            {formData.features.map((feature, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 md:border-0 md:p-0">
                <div className="flex justify-between items-center mb-3 md:hidden">
                  <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                    Característica {index + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeFeature(index)}
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                    title="Quitar característica"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_1fr_auto] gap-4 md:items-end">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Icono
                    </label>
                    <div className="flex items-center gap-2">
                      <FeatureIconPreview iconName={feature.icon} />
                      <select
                        value={feature.icon}
                        onChange={(e) => handleFeatureChange(index, 'icon', e.target.value)}
                        className="flex-1 md:flex-none px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      >
                        {ICON_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Etiqueta
                    </label>
                    <input
                      type="text"
                      value={feature.label}
                      onChange={(e) => handleFeatureChange(index, 'label', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder="Superficie, Dormitorios..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Valor
                    </label>
                    <input
                      type="text"
                      value={feature.value}
                      onChange={(e) => handleFeatureChange(index, 'value', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder="Ej: 3, 150 m²"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => removeFeature(index)}
                    className="hidden md:block p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    title="Quitar característica"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={addFeature}
            className="mt-4 flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 border border-dashed border-blue-300 rounded-lg hover:bg-blue-50"
          >
            + Agregar característica
          </button>
        </div>

        {/* Imágenes */}
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">Imágenes</h2>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-4">
              Subir imágenes nuevas (máximo 20 por vez)
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageChange}
                className="hidden"
                id="imageUpload"
              />
              <label
                htmlFor="imageUpload"
                className="cursor-pointer inline-flex items-center px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Upload className="w-5 h-5 mr-2" />
                Seleccionar imágenes
              </label>
              <p className="text-gray-500 mt-2 text-sm">
                Arrastra imágenes aquí o haz clic para seleccionar
              </p>
            </div>
          </div>
          
          {/* Imágenes ya guardadas */}
          {existingImages.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-2">Fotos actuales ({existingImages.length})</h3>
              <p className="text-xs text-gray-500 mb-4">Arrastrá las fotos para reordenarlas. La primera es la principal.</p>
              <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleExistingDragEnd}>
                <SortableContext items={existingImages} strategy={rectSortingStrategy}>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {existingImages.map((imageUrl, index) => (
                      <SortableImage
                        key={imageUrl}
                        id={imageUrl}
                        src={formatImageUrl(imageUrl)}
                        isMain={index === 0}
                        onRemove={() => removeExistingImage(index)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          )}

          {/* Previews de imágenes nuevas */}
          {imagePreviews.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-800 mb-2">Fotos nuevas a agregar ({imagePreviews.length})</h3>
              <p className="text-xs text-gray-500 mb-4">Arrastrá para reordenarlas.</p>
              <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleNewImagesDragEnd}>
                <SortableContext items={imagePreviews.map((_, i) => String(i))} strategy={rectSortingStrategy}>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {imagePreviews.map((preview, index) => (
                      <SortableImage
                        key={index}
                        id={String(index)}
                        src={preview}
                        isMain={existingImages.length === 0 && index === 0}
                        onRemove={() => removeImage(index)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          )}
        </div>
        
        {/* Botones de acción */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/admin/properties')}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Guardando...
              </>
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                {isEditMode ? 'Actualizar Propiedad' : 'Crear Propiedad'}
              </>
            )}
          </button>
        </div>
      </form>

      {/* Barra flotante de guardado rápido - evita tener que bajar hasta el final */}
      {isDirty && !saving && (
        <div className="fixed bottom-0 left-0 right-0 md:left-64 z-40 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] px-4 py-3 md:px-8 animate-fade-in">
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
            <p className="text-sm text-gray-600 hidden sm:flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0"></span>
              Tenés cambios sin guardar
            </p>
            <div className="flex gap-3 ml-auto">
              <button
                type="button"
                onClick={() => navigate('/admin/properties')}
                className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                form="property-form"
                className="px-5 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 font-medium"
              >
                <Save className="w-4 h-4" />
                Guardar cambios
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Overlay minimalista mientras guarda */}
      {saving && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl px-8 py-6 flex items-center gap-4">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
            <p className="font-medium text-gray-800">
              {isEditMode ? 'Actualizando datos, esperá...' : 'Creando propiedad, esperá...'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyForm;